import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { MapPin, Users, Star, Bell, BellOff } from "lucide-react";
import type { EventSession } from "../types";
import { GlassCard } from "./GlassCard";
import { useAppStore } from "../store/useAppStore";
import { sessionsService } from "../services/sessionsService";
import { maxPlatform } from "../services/maxPlatform";
import { useTranslation } from "react-i18next";
import { intlLocale } from "../i18n";
import { formatVenueDate, formatVenueTime } from "../utils/venueDateTime";
import { htmlToPlainText } from "../utils/safeHtml";
import { localizeProgrammeTag, programmeTagClass } from "../utils/programmeTags";

interface SessionCardProps {
  session: EventSession;
  showDate?: boolean;
}

const SESSION_TYPE_COLORS: Record<string, string> = {
  presentation: "bg-blue-100 text-blue-700",
  workshop: "bg-amber-100 text-amber-700",
  panel: "bg-purple-100 text-purple-700",
  masterclass: "bg-amber-100 text-amber-700",
  networking: "bg-pink-100 text-pink-700",
  keynote: "bg-blue-100 text-blue-800",
};

export function SessionCard({ session, showDate = false }: SessionCardProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAppStore();
  const [isFavorite, setIsFavorite] = useState(Boolean(session.isFavorite));
  const [reminderOn, setReminderOn] = useState(session.notificationEnabled !== false);
  const tz = session.venueTimeZone ?? "Europe/Moscow";
  const loc = intlLocale(i18n.language);

  useEffect(() => {
    setIsFavorite(Boolean(session.isFavorite));
    if (session.notificationEnabled !== undefined) {
      setReminderOn(session.notificationEnabled);
    }
  }, [session.id, session.isFavorite, session.notificationEnabled]);

  const handleClick = () => {
    navigate(`/schedule/${session.id}`);
  };

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isFavorite) {
      await sessionsService.removeFromFavorites(user.id, session.id, session.eventId);
      setIsFavorite(false);
      setReminderOn(true);
    } else {
      await sessionsService.addToFavorites(user.id, session.id, session.eventId);
      setIsFavorite(true);
      setReminderOn(true);
      maxPlatform.hapticFeedback("success");
    }
  };

  const handleToggleReminder = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isFavorite) return;
    const next = !reminderOn;
    await sessionsService.setFavoriteNotification(
      user.id,
      session.eventId,
      session.id,
      next,
    );
    setReminderOn(next);
    maxPlatform.hapticFeedback("light");
  };

  return (
    <GlassCard
      variant="default"
      padding="md"
      className="cursor-pointer active:scale-[0.98] transition-transform"
      onClick={handleClick}
    >
      <div className="flex gap-3">
        <div className="flex flex-col items-center flex-shrink-0 w-16">
          {showDate && (
            <div className="text-[11px] text-gray-500 mb-1">
              {formatVenueDate(session.startTime, loc, tz)}
            </div>
          )}
          <div className="text-[16px] font-semibold text-indigo-600">
            {formatVenueTime(session.startTime, loc, tz)}
          </div>
          <div className="text-[11px] text-gray-400">{formatVenueTime(session.endTime, loc, tz)}</div>
        </div>

        <div className="w-px bg-gray-200 flex-shrink-0" />

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="text-[14px] text-gray-900 line-clamp-2 flex-1">{htmlToPlainText(session.title)}</h3>
            <div className="flex items-center gap-0.5 flex-shrink-0">
              {isFavorite && (
                <button
                  type="button"
                  onClick={handleToggleReminder}
                  className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                  title={reminderOn ? t("visitor.reminder_enabled") : t("visitor.reminder_disabled")}
                >
                  {reminderOn ? (
                    <Bell size={16} className="text-indigo-500" />
                  ) : (
                    <BellOff size={16} className="text-gray-400" />
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={handleToggleFavorite}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Star
                  size={18}
                  className={
                    isFavorite ? "text-amber-500 fill-amber-500" : "text-gray-300"
                  }
                />
              </button>
            </div>
          </div>

          <div className="mb-2 flex flex-wrap gap-1">
            {(session.tags?.length
              ? session.tags
              : [
                  t(`visitor.session_type_${session.type}`, {
                    defaultValue: session.type,
                  }),
                ]
            ).map((tag) => {
              const label = session.tags?.length
                ? localizeProgrammeTag(tag, i18n.language)
                : tag;
              return (
              <span
                key={tag}
                className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${
                  session.tags?.length
                    ? programmeTagClass(tag)
                    : SESSION_TYPE_COLORS[session.type] || "bg-gray-100 text-gray-700"
                }`}
              >
                {label}
              </span>
              );
            })}
          </div>

          {session.location && (
            <div className="flex items-center gap-1 text-[12px] text-gray-500 mb-2">
              <MapPin size={12} />
              <span className="line-clamp-1">{session.location}</span>
            </div>
          )}

          {session.speakers.length > 0 && (
            <div className="flex items-center gap-1.5 text-[12px] text-gray-600">
              <Users size={12} className="text-gray-400" />
              <span className="line-clamp-1">
                {session.speakers.map((s) => s.fullName).join(", ")}
              </span>
            </div>
          )}
        </div>
      </div>
    </GlassCard>
  );
}
